import { Button, Popup } from 'devextreme-react';
import Form, {
  ButtonItem,
  ButtonOptions,
  CustomRule,
  GroupItem,
  Label,
  RequiredRule,
  SimpleItem,
} from 'devextreme-react/form';
import DataSource from 'devextreme/data/data_source';
import { formatDate } from 'devextreme/localization';
import moment from 'moment';
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';
import useSWR from 'swr';
import { postApi, putApi } from '../../apiUtils';
import { FileFigureList } from '../../components/fileManager/FileFigureList';
import { IAfterSubmitMode } from '../../components/formSuccessButtons/FormSuccessButtons';
import { Loader } from '../../components/loader/Loader';
import { useAssetClass } from '../../contexts/assetClass';
import { useLoading } from '../../contexts/loadingContext';
import { useTranslation } from '../../contexts/translation';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { useSafeGoBack } from '../../hooks/useSafeGoBack';
import useUnsavedChangesWarning from '../../hooks/useUnsavedChangesWarning';
import { swrConfig } from '../../pages/Assets/AssetsEdit';
import { TaskChangeStatus } from '../../pages/Tasks/TaskChangeStatus';
import { Asset } from '../../types/asset.types';
import { CapacityCategory } from '../../types/capacityCategory.types';
import { DocumentType } from '../../types/documentType.types';
import { FaultType } from '../../types/faultType';
import { File } from '../../types/file.types';
import { LocationState } from '../../types/locationState.types';
import { DetailPageParams } from '../../types/pageParams.types';
import { Person } from '../../types/person.types';
import { Task } from '../../types/task.types';
import { TaskName, TaskNameListItem } from '../../types/taskNames.types';
import { TaskRatings } from '../../types/taskRating.types';
import { TaskStatus, TaskStatusApiResponse } from '../../types/TaskStatus';
import { getUserColorClass, UserColor } from '../../types/userColor.types';
import { WorkSheet } from '../../types/workSheet.types';
import { API_URL, fetcher } from '../../utils/apiUrl';
import {
  dateFieldOptionsWithSerialization,
  dateTimeFieldOptionsWithSerialization,
  displayDateTimeFormat_CZ,
  displayDateTimeFormat_EN,
} from '../../utils/dates.types';
import { STORAGE_KEYS } from '../../utils/localStorageKeys';
import './TasksForm.scss';

export interface TaskInputsNew {
  id?: number;
  assetTopLevelParenId?: number;
  assetId?: number;
  assetTypeId?: number;
  typeId?: number;
  typeName?: string;
  typeNameTranslated?: string;
  capacityCategoryId?: number;
  taskName?: string;
  requiredDate?: Date | null | string;
  creationTime?: Date | null | string;
  downtimeFrom?: Date | null | string;
  downtimeTo?: Date | null | string;
  description?: string;
  diagnose?: string;
  statusName?: string;
  statusNameTranslated?: string;
  responsiblePersonId?: number;
  assetCycleRunId?: number | null;
  faultTypeId?: number;

  typeTaskNameCombo?: boolean;
  typeShowDowntime?: boolean;
  typeTaskShowFaultType?: boolean;
}

export const dxSelectBoxConfig = {
  searchEnabled: true,
  searchModeOption: 'contains',
  searchExprOption: 'name',
  searchTimeoutOption: 200,
  showClearButton: true,
};

/**
 * Atributy Assetu, podle kterých se navíc vyhledává v dropdownech Linka (assetTopLevelParenId) a Asset (assetId).
 * V itemTemplate se zobrazí jen ten atribut, kterým položka skutečně prošla hledáním.
 */
export const assetSearchAttributes: { field: keyof Asset; label: string }[] = [
  { field: 'typeName', label: 'Type' },
  { field: 'model', label: 'Model' },
  { field: 'serialNumber', label: 'Serial number' },
  { field: 'inventoryNumber', label: 'Inventory number' },
  { field: 'centre', label: 'Centre' },
  { field: 'building', label: 'Building' },
  { field: 'room', label: 'Room' },
  { field: 'assetLocationName', label: 'Location' },
  { field: 'producedBySupplierName', label: 'Produced by supplier' },
  { field: 'deliveredBySupplierName', label: 'Delivered by supplier' },
];

/**
 * Sestaví opakovaný query parametr, např. buildRepeatedQueryParam('fLvlId', [1, 2]) => '&fLvlId=1&fLvlId=2'.
 */
const buildRepeatedQueryParam = (paramName: string, values?: number[]): string =>
  values && values.length > 0 ? values.map((value) => `&${paramName}=${value}`).join('') : '';

interface Props {
  defaultFormData: TaskInputsNew;
  isEdit?: boolean;
  defaultIsTaskNameCombo?: boolean;
  defaultShowDowntime?: boolean;
  defaultShowFaultType?: boolean;
  onSubmitDone?: () => void;
  isReadOnly: boolean;
  oldRequiredDate?: Date | null | string;
  transFormName: string;
  mutateTask?: any;
  isCopy?: boolean;
  worksheets?: WorkSheet[];
  defaultDocumentTypeId?: number | null;
  assetLevelIds?: number[];
  parentAssetLevelIds?: number[];
}

interface SectionTitles {
  titleLineAsset: ReactElement;
  titleCategory: ReactElement;
  titleTask: ReactElement;
  titleDowntime: ReactElement;
}

export default function TaskForm({
  onSubmitDone,
  defaultFormData,
  isEdit,
  defaultIsTaskNameCombo,
  defaultShowDowntime,
  defaultShowFaultType,
  isReadOnly,
  oldRequiredDate,
  transFormName,
  mutateTask,
  isCopy,
  worksheets,
  defaultDocumentTypeId,
  assetLevelIds,
  parentAssetLevelIds,
}: Props) {
  const { translate, lang } = useTranslation();
  const { assetClass, changeAssetClass, refetchAssetClass } = useAssetClass();
  const { startLoading, loading, stopLoading } = useLoading();
  const { state }: { state: LocationState | undefined } = useLocation();
  const history = useHistory();
  const { id } = useParams<DetailPageParams>();
  const [formData, setFormData] = useState<TaskInputsNew>(defaultFormData);
  const [isTaskNameCombo, setTaskNameCombo] = useState<boolean>(defaultIsTaskNameCombo!);
  const [isShowDowntime, setShowDowntime] = useState<boolean>(defaultShowDowntime!);
  const [isShowFaultType, setShowFaultType] = useState<boolean>(defaultShowFaultType!);
  const [isAssetChangeDialogVisible, setAssetChangeDialogVisible] = useState<boolean>(false);
  const [afterSubmitMode, setAfterSubmitMode] = useState<IAfterSubmitMode>(IAfterSubmitMode.NEW);
  const [userColorClass, setUserColorClass] = useState<string | null>(null);
  const [selectedDocumentTypeId, setSelectedDocumentTypeId] = useState<number | null>(null);
  const [targetTaskStatusIdForCreate, setTargetTaskStatusIdForCreate] = useState<number | null>(
    null,
  );
  const [localTaskNames, setLocalTaskNames] = useState<TaskNameListItem[]>();
  const [sectionTitles, setSectionTitles] = useState<SectionTitles>({
    titleLineAsset: <>${translate!('Asset detail', transFormName)}</>,
    titleCategory: <>${translate!('Category detail', transFormName)}</>,
    titleTask: <>${translate!('Request detail', transFormName)}</>,
    titleDowntime: <>${translate!('Production downtime', transFormName)}</>,
  });
  const [contextState] = useLocalStorage(STORAGE_KEYS.CONTEXT_STATE, {});
  const transFormNameAssetClass = 'Task.AssetClassChange';
  const {
    data: task,
    error: errorTask,
    isValidating: taskLoading,
  } = useSWR<Task>(id ? `${API_URL.TASK_ITEM}?id=${id}` : null, fetcher, swrConfig);

  const { data: userColors } = useSWR<UserColor[]>(
    `${API_URL.USER_COLOR_LIST}`,
    fetcher,
    swrConfig,
  );

  useEffect(() => {
    id &&
      task &&
      task?.assetClassId &&
      task?.assetClassId !== assetClass &&
      setAssetChangeDialogVisible(true);
  }, []);

  const AssetClassChangeDialog = () => {
    const handleAssetClassChange = () => {
      // @ts-ignore
      changeAssetClass(task?.assetClassId);
      // @ts-ignore
      refetchAssetClass();
      setAssetChangeDialogVisible(false);
    };

    return (
      <Popup
        visible={isAssetChangeDialogVisible}
        dragEnabled={false}
        closeOnOutsideClick={false}
        onHiding={() => setAssetChangeDialogVisible(false)}
        showTitle
        title={translate!('Change asset class', transFormNameAssetClass)}
        width={'25%'}
        height="auto"
      >
        <p>
          {translate!(
            'The selected task has a different asset class than the currently selected. Do you want to change asset class?',
            transFormNameAssetClass,
          )}
        </p>
        <div style={{ display: 'inline-block', marginRight: '10px' }}>
          <Button text={translate!('Yes', '')} type={'success'} onClick={handleAssetClassChange} />
        </div>
        <Button text={translate!('No', '')} onClick={() => history.goBack()} />
      </Popup>
    );
  };
  const formRef = useRef<Form>(null);
  const topLevelAssetSearchValueRef = useRef<string>('');
  const assetSearchValueRef = useRef<string>('');
  const hasExplicitBackRoute = !!(state?.backRoute || (contextState && contextState.backRoute));
  const backRoute = state?.backRoute
    ? state.backRoute
    : (contextState && contextState.backRoute) ?? '/tasks';
  // Pokud nevíme, odkud uživatel na formulář přišel, vrátíme ho na skutečnou předchozí
  // stránku - a pokud žádná v rámci aplikace neexistuje (formulář byl otevřen jako první),
  // přesměrujeme na home místo natvrdo na '/tasks'.
  const safeGoBack = useSafeGoBack('/home');
  const goToBackRoute = () => {
    if (hasExplicitBackRoute) {
      history.push({ pathname: backRoute, state: { loadSettingFromSession: true } });
    } else {
      safeGoBack();
    }
  };
  const [
    prompt,
    setDirty,
    setClean,
    handleBackButton,
    handleHistoryGoBackButton,
    handleDirtyWithCallback,
    isDirty,
  ] = useUnsavedChangesWarning(backRoute, () => {
    if (!formRef) return;
    if (formRef.current!.instance.validate().isValid)
      onSubmit(null).then((result) => {
        if (result) goToBackRoute();
        else setDirty();
      });
  });

  /**
   * Načte se seznam topLevelAssetů - linky
   */
  const { data: topLevelAssets, error: topLevelAssetsError } = useSWR<Asset[]>(
    `${
      API_URL.ASSET_LIST_BY_CLASS_TOP_LEVEL
    }?classId=${assetClass}&activeOnly=true&visibleOnly=true${buildRepeatedQueryParam(
      'fLvlId',
      parentAssetLevelIds,
    )}`,
    fetcher,
    swrConfig,
  );

  /**
   * Načte seznam assetů, pokud je vybrána linka (assetTopLevelParenId), filtrují se assety podle této linky
   */
  const {
    data: assets,
    error: assetsError,
    isValidating: isValidatingAssets,
  } = useSWR<Asset[]>(
    formData.assetTopLevelParenId
      ? `${API_URL.ASSET_LIST_BY_CLASS}?classId=${assetClass}&parentId=${
          formData.assetTopLevelParenId
        }${
          !assetLevelIds || assetLevelIds.length === 0
            ? `&assetToInclude=${formData.assetTopLevelParenId}`
            : ''
        }${buildRepeatedQueryParam('fLvlId', assetLevelIds)}`
      : `${API_URL.ASSET_LIST_BY_CLASS}?classId=${assetClass}${buildRepeatedQueryParam(
          'fLvlId',
          assetLevelIds,
        )}`,
    fetcher,
    swrConfig,
  );

  useEffect(() => {
    if (assets) {
      const asset = assets.find((item) => item.id === task?.assetId);
      if (asset) {
        setFormData((prev) => ({ ...prev, assetTypeId: asset?.typeId }));
      }
    }
  }, [assets]);

  // přednastaví Linku a Asset, pokud se task vytváří z assetu
  useEffect(() => {
    if (!assets || !state || !state.assetId) return;
    const asset = assets.find((asset: Asset) => asset.id === Number(state.assetId));
    if (asset)
      setFormData((prev) => ({
        ...prev,
        assetTopLevelParenId: asset.topLevelParenId,
        assetId: Number(asset.id),
      }));
  }, [assets, state]);

  /**
   * Načte seznam documentTypes.
   */
  const { data: documentTypes, error: documentTypesError } = useSWR<DocumentType[]>(
    `${API_URL.GET_LIST_FOR_TASK_CREATE}?parentId=${assetClass}&taskCreateDesktop=true&visibleOnly=true&validateForUser=true`,
    fetcher,
    swrConfig,
  );

  /**
   * Načte seznam capacityCategories.
   */
  const { data: capacityCategories, error: capacityCategoriesError } = useSWR<CapacityCategory[]>(
    `${API_URL.CAPACITY_CATEGORY_LIST}?parentId=${assetClass}`,
    fetcher,
    swrConfig,
  );

  /**
   * Načte seznam faul types.
   */
  const { data: faultTypes, error: faultTypesError } = useSWR<FaultType[]>(
    `${API_URL.FAULT_TYPE_LIST}`,
    fetcher,
    swrConfig,
  );

  const { data: statusesToCreate } = useSWR<TaskStatus[]>(
    selectedDocumentTypeId
      ? `${API_URL.TASK_PROCESS_CONFIG_LIST_BY_DOC_TYPE}?documentTypeId=${selectedDocumentTypeId}`
      : null,
    fetcher,
    swrConfig,
  );

  /**
   * Načte seznam taskNames - pro zadání přes combo. TaskNames se načtou pouze pokud máme vybraný documentType,
   * Asset a zároveň pokud se má taskName jako combo zobrazovat. TaskNames se načítají podle assetTypeId.
   */
  const {
    data: taskNames,
    error: taskNamesError,
    isValidating: isValidatingTaskNames,
  } = useSWR<TaskName[]>(
    isTaskNameCombo && formData.typeId && formData.assetTypeId
      ? `${API_URL.TASK_NAME_FILTERED}?documentTypeId=${formData.typeId}&assetTypeId=${
          formData.assetTypeId
        }${
          formData.capacityCategoryId ? `&capacityCategoryId=${formData.capacityCategoryId}` : ''
        }&visibleOnly=true`
      : null,
    fetcher,
    swrConfig,
  );

  /**
   * Načte zodpovědné osoby pro task action. Pouze v editaci.
   */
  const { data: taskActionRespPersons } = useSWR<Person[]>(
    isEdit && formData.id && formData.capacityCategoryId
      ? `${API_URL.CAPACITY_CATEGORY_PERSON_LIST}?parentId=${formData.capacityCategoryId}`
      : null,
    fetcher,
    swrConfig,
  );

  // Načteme číselník - Persons
  const { data: responsiblePersons, error: errorResponsiblePersons } = useSWR<Person[]>(
    API_URL.PERSON_FROM_THIS_APP_LIST,
    fetcher,
  );

  /**
   * Načteme stavy pro akční tlačítka. Pouze v editaci.
   */
  const {
    data: statuses,
    mutate: mutateStatuses,
    isValidating: isStatusesValidating,
  } = useSWR<TaskStatusApiResponse>(
    isEdit && formData.id ? `${API_URL.TASK_PROCESS_CONFIG}?taskId=${formData.id}` : null,
    fetcher,
    swrConfig,
  );

  // Načtení rating ikon
  const { data: taskRatings } = useSWR<TaskRatings>(
    statuses && statuses?.find((s: any) => s?.requireRating === true)
      ? `${API_URL.TASK_RATING_LIST}?visibleOnly=true`
      : null,
    fetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      onSuccess: (result) => {
        let sortedArr = result.sort((a: any, b: any) =>
          a.rating > b.rating ? 1 : b.rating > a.rating ? -1 : 0,
        );
        return sortedArr;
      },
    },
  );

  useEffect(() => {
    if (!task || !documentTypes) return;
    setFormData({
      ...task,
      typeId: defaultDocumentTypeId || task.typeId,
      taskName: task.taskName,
      description: task.description,
      responsiblePersonId: task.responsiblePersonId,
      requiredDate: task.requiredDate,
    });

    const docType = documentTypes.find((dt) => dt.id === (defaultDocumentTypeId || task.typeId));
    if (docType)
      setTaskNameCombo(docType.taskNameCombo === undefined ? false : docType.taskNameCombo);
  }, [taskLoading, task, documentTypes]);

  // Načteme subory jen když je edit
  const { data: files } = useSWR<File[]>(
    isEdit && formData.id ? `${API_URL.FILE_LIST}?entity=${'Task'}&entityId=${formData.id}` : null,
    fetcher,
  );

  const handleStatusChanged = () => {
    if (mutateTask) mutateTask();
  };

  const onSubmit = async (e) => {
    if (e !== null) e.preventDefault();
    if (!formData) return;
    let isOk = false;

    setClean();
    if (isEdit) isOk = await submitPut();
    else isOk = await submitPost();
    if (onSubmitDone) {
      onSubmitDone();
      mutateStatuses();
    }

    return isOk;
  };

  const submitPost = async () => {
    let isOk = false;
    startLoading();
    await postApi({
      url: API_URL.TASK_POST,
      data: targetTaskStatusIdForCreate
        ? { ...formData, targetTaskStatusIdForCreate: targetTaskStatusIdForCreate }
        : formData,
      callAfterSuccess: (data) => {
        isOk = true;
        if (afterSubmitMode === IAfterSubmitMode.NEW) {
          setFormData(defaultFormData);
          return;
        }
        if (afterSubmitMode === IAfterSubmitMode.LIST) goToBackRoute();
        if (afterSubmitMode === IAfterSubmitMode.CONTINUE) {
          history.push(`/tasks/edit/${data.id}`);
        }
      },
    });
    stopLoading();

    return isOk;
  };

  const submitPut = async () => {
    let isOk = false;
    startLoading();
    await putApi({
      url: API_URL.TASK_PUT,
      data: formData,
      callAfterSuccess: async () => {
        isOk = true;
      },
    });
    stopLoading();
    return isOk;
  };

  /**
   * Změna linky (TopLevelAssetId). Je potřeba profiltrovat seznam assetů podle vybrané linky.
   * Nastavením formData.assetTopLevelParenId se provede přenačtení číselníku assets.
   * @param data: Data z dropDownListu. (TopLevelParentId)
   */
  const handleTopLevelAssetChanged = (data) => {
    setFormData({ ...formData, assetTopLevelParenId: data.value });
  };

  /**
   * Změna assetu. Při výběru assetu se nastaví příslučná linka. Linka se pozná podle
   * atributu assetTopLevelParenId, který má asset. Dále podle assetu nastavíe assetTypeId,
   * který je potřeba pro načítání taskNames do comba.
   * @param data: Data z dropDownListu (AssetId)
   */
  const handleAssetChanged = (data) => {
    if (!assets) return;
    const asset = assets.find((x) => x.id === data.value);
    if (!asset) {
      setFormData({ ...formData, assetId: undefined });
      return;
    }
    // Asset má atribut assetTopLevelParenId, podle kterého vybereme linku v dropdownlistu
    const assetTopLevelParenId = asset.topLevelParenId;
    // AssetTypeId pro načtení TaskNames do comba
    const assetTypeId = asset.typeId;
    setFormData({ ...formData, assetId: data.value, assetTopLevelParenId, assetTypeId });
  };

  /**
   * Pokud se jedná o editační formulář, potřebujeme již při načtení formuláře nastavit assetTypeI
   */
  useEffect(() => {
    if (!isEdit || !assets) return;
    const asset = assets.find((x) => x.id === formData.assetId);
    if (!asset) return;
    setFormData({ ...formData, assetTypeId: asset.typeId });
  }, [assets]);

  const handleTasknameListChanges = () => {
    if (!taskNames || !formData) return;

    if (!formData.taskName || taskNames.find((x) => x.name === formData.taskName!)) {
      setLocalTaskNames([...taskNames]);
      return;
    }

    if (formData.taskName === '') return;

    const newTaskName: TaskNameListItem = {
      name: formData.taskName!,
    };

    setLocalTaskNames([...taskNames, newTaskName]);
  };

  /**
   * Vytvoříme si lokální taskNames, protože do nich potřebujeme přidat taskName, který je na tasku, jelikož nemusí být v DB
   */
  useEffect(() => {
    handleTasknameListChanges();
  }, [taskNames]);

  /**
   * Při úprave formulářových dat upravíme titulky sekcí.
   */
  const handleChangeTitles = () => {
    const topLevelAsset = topLevelAssets?.find((x) => x.id === formData.assetTopLevelParenId);
    const asset = assets?.find((x) => x.id === formData.assetId);
    const documentType = documentTypes?.find((x) => x.id === formData.typeId);
    const capacityCategory = capacityCategories?.find((x) => x.id === formData.capacityCategoryId);

    const redStar = <span style={{ color: '#f51c36', fontWeight: 600 }}>*</span>;

    // Titulek linka, asset
    let titleLineAsset = (
      <>
        {redStar}
        {`${translate!('Asset', transFormName)}`}
      </>
    );
    if (topLevelAsset || asset) {
      const label1 = topLevelAsset ? topLevelAsset.name : '';
      const label2 = asset ? (
        asset.name
      ) : (
        <>
          {redStar}
          {`${translate!('Asset', transFormName)}`}
        </>
      );
      titleLineAsset = (
        <>
          {label1}
          {label1 ? ', ' : ''}
          {label2}
        </>
      );
    }

    // Kategorie
    let titleCategory = (
      <>
        {redStar}
        {`${translate!('Type', transFormName)}`},{redStar}
        {`${translate!('Category', transFormName)}`}
      </>
    );
    if (documentType || capacityCategory) {
      const label1 = documentType ? (
        documentType.name
      ) : (
        <>
          {redStar}
          {`${translate!('Type', transFormName)}`}
        </>
      );
      const label2 = capacityCategory ? (
        capacityCategory.name
      ) : (
        <>
          {redStar}
          {`${translate!('Category', transFormName)}`}
        </>
      );
      titleCategory = (
        <>
          {label1}
          {label1 ? ', ' : ''}
          {label2}
        </>
      );
    }

    // Task a datum
    let titleTask = (
      <>
        {redStar}
        {`${translate!('Task name', transFormName)}`}
      </>
    );
    if (formData.taskName || formData.requiredDate) {
      const label1 = formData.taskName ? (
        formData.taskName
      ) : (
        <>
          {redStar}
          {`${translate!('Task name', transFormName)}`}
        </>
      );
      const label2 = formData.requiredDate
        ? formatDate(new Date(formData.requiredDate), 'shortDate')
        : '';
      titleTask = (
        <>
          {label1}
          {label1 ? ', ' : ''}
          {label2}
        </>
      );
    }

    // Downtime
    let titleDowntime = (
      <>
        {redStar}
        {`${translate!('Downtime from', transFormName)}`},{redStar}
        {`${translate!('Downtime to', transFormName)}`}
      </>
    );
    if (formData.downtimeFrom || formData.downtimeTo) {
      const label1 = formData.downtimeFrom ? (
        formatDate(new Date(`${formData.downtimeFrom}`), 'shortDateShortTime')
      ) : (
        <>
          {redStar}
          {`${translate!('Downtime from', transFormName)}`}
        </>
      );
      const label2 = formData.downtimeTo
        ? formatDate(new Date(`${formData.downtimeTo}`), 'shortDateShortTime')
        : '';
      titleDowntime = (
        <>
          {translate!('Production downtime', transFormName)}
          {` ${label1}`}
          {label1 ? ', ' : ''}
          {label2}
        </>
      );
    }

    setSectionTitles({ ...sectionTitles, titleLineAsset, titleCategory, titleTask, titleDowntime });
  };

  /**
   * Změna DocumentType. Na základě documentType je třeba zobrazit/skrýt (obecně upravit) prvky formuláře.
   * @param data:  Data z dropDownListu (DocumentTypeId)
   */
  const handleDocumentTypeChanged = (data) => {
    if (!data.event) return;
    if (!documentTypes) return;

    setSelectedDocumentTypeId(data.value);

    const documentType = documentTypes.find((x) => x.id === data.value);
    if (!documentType) {
      setFormData({ ...formData, typeId: undefined });
      return;
    }

    // Nastavíme defaulní hodnoty formuláře
    let newFormData = { ...formData, typeId: documentType.id };
    const now = new Date();
    newFormData = {
      ...newFormData,
      taskName: '',
      requiredDate: documentType.requiredDateFilledByDefault
        ? moment(now).format('Y-MM-DD HH:mm')
        : null,
      downtimeFrom: documentType.downTimeFromFilledByDefault
        ? moment(now).format('Y-MM-DD HH:mm')
        : null,
    };
    setFormData(newFormData);

    // Nastavíme, jesli se má TaskName zobrazovat jako textbox nebo jako dropdown
    setTaskNameCombo(documentType ? documentType.taskNameCombo : false);
    // Zobrazíme/skryjeme odstávkové prvky
    setShowDowntime(documentType ? documentType.showDowntime : false);
    // Zobrazíme/skryjeme fault type
    setShowFaultType(documentType ? documentType.taskShowFaultType : false);
  };

  /**
   * Pokud se jedná o editační formulář, potřebujeme již při načtení formuláře nastavit viditelnost
   * prvků v závislosti na documentType.
   */
  useEffect(() => {
    if (!isEdit || !documentTypes) return;

    if (formData.typeTaskNameCombo) setTaskNameCombo(formData.typeTaskNameCombo);
    if (formData.typeShowDowntime) setShowDowntime(formData.typeShowDowntime);
    if (formData.typeTaskShowFaultType) setShowFaultType(formData.typeTaskShowFaultType);
  }, [documentTypes, formData]);

  const prepareTasknamesData = () => {
    // @ts-ignore
    return localTaskNames?.some((item: TaskName) => item.groupName)
      ? new DataSource({
          store: {
            type: 'array',
            // @ts-ignore
            data: localTaskNames.map((taskName: TaskName) => ({
              ...taskName,
              groupName: taskName.groupName
                ? taskName.groupName
                : translate!('Others', transFormName),
            })),
            key: 'name',
          },
          group: 'groupName',
        })
      : localTaskNames ?? [];
  };

  // Validační funkce, která se volá při provedení TaskAction
  // Funkce se pokusí provést Edit, pokud se nepodaří, TaskAction se nevykoná
  const customValidationFunctionActions = async (): Promise<boolean> => {
    if (!isDirty) return true;

    if (!formRef || !formRef.current!.instance.validate().isValid) return false;

    const result = await onSubmit(null).then((result) => {
      return result;
    });

    if (!result) {
      setDirty();
      return false;
    }

    return true;
  };

  // Vygeneruje string pro textareu činnosti
  const renderActivities = () => {
    if (!worksheets) return;
    let content = '';
    const dateFormat = lang === 'en-US' ? displayDateTimeFormat_EN : displayDateTimeFormat_CZ;
    worksheets
      .filter((x) => x.description && x.description.trim() !== '')
      .forEach((item) => {
        content += `--- ${moment(item.activityStarted).format(dateFormat)} ${
          item.operatorName
        } ---\n${item.description || ``}\n`;
      });
    return content;
  };

  useEffect(() => {
    if (!userColors || !defaultFormData || !defaultFormData.responsiblePersonId) return;
    handleResponsiblePersonChanged(userColors, defaultFormData.responsiblePersonId);
  }, [userColors]);

  const handleResponsiblePersonChanged = (userColors: UserColor[], userId: number) => {
    const colorClass = getUserColorClass(userColors, userId);
    setUserColorClass(colorClass);
  };

  if (
    topLevelAssetsError ||
    assetsError ||
    documentTypesError ||
    capacityCategoriesError ||
    taskNamesError ||
    errorResponsiblePersons ||
    faultTypesError
  )
    return <>Došlo k chybě na straně API</>;
  if (
    !topLevelAssets ||
    !documentTypes ||
    !capacityCategories ||
    !responsiblePersons ||
    !faultTypes ||
    !userColors
  )
    return <Loader />;
  if (isValidatingAssets || isValidatingTaskNames || isStatusesValidating) return <Loader />;

  return (
    <>
      <form onSubmit={onSubmit}>
        <Form
          formData={formData}
          colCount={3}
          onFieldDataChanged={(e) => {
            if (e.dataField === 'worksheetsSimpleList') return;
            handleChangeTitles();
            setDirty();
          }}
          ref={formRef}
        >
          <SimpleItem
            dataField={'assetTopLevelParenId'}
            editorType={'dxSelectBox'}
            editorOptions={{
              ...dxSelectBoxConfig,
              valueExpr: 'id',
              displayExpr: 'name',
              searchExpr: [
                'typeName',
                'name',
                'model',
                'serialNumber',
                'inventoryNumber',
                'centre',
                'building',
                'room',
                'assetLocationName',
                'producedBySupplierName',
                'deliveredBySupplierName',
              ],
              dataSource: topLevelAssets,
              readOnly: isReadOnly,
              onValueChanged: handleTopLevelAssetChanged,
              onOptionChanged: (e: any) => {
                if (e.name === 'text') topLevelAssetSearchValueRef.current = e.value ?? '';
              },
              itemTemplate: (data: Asset, _index: number, element: any) => {
                const searchValue = topLevelAssetSearchValueRef.current.trim().toLowerCase();
                const matchedAttributesHtml = searchValue
                  ? assetSearchAttributes
                      .filter(({ field }) =>
                        (data[field] ?? '').toString().toLowerCase().includes(searchValue),
                      )
                      .map(
                        ({ field, label }) =>
                          `<b>${translate!(label, transFormName)}:</b> ${data[field]}`,
                      )
                      .join(' ')
                  : '';
                const matchedAttributes = matchedAttributesHtml
                  ? `<div style="font-size: 11px;"><i>${matchedAttributesHtml}</i></div>`
                  : '';
                element.innerHTML = `<div>${data?.name ?? ''}</div>${matchedAttributes}`;
              },
            }}
          >
            <Label text={translate!('Line', transFormName)} />
          </SimpleItem>
          <SimpleItem
            dataField={'assetId'}
            editorType={'dxSelectBox'}
            editorOptions={{
              ...dxSelectBoxConfig,
              valueExpr: 'id',
              displayExpr: 'name',
              searchExpr: [
                'typeName',
                'name',
                'model',
                'serialNumber',
                'inventoryNumber',
                'centre',
                'building',
                'room',
                'assetLocationName',
                'producedBySupplierName',
                'deliveredBySupplierName',
              ],
              dataSource: assets,
              readOnly: isReadOnly,
              onValueChanged: handleAssetChanged,
              onOptionChanged: (e: any) => {
                if (e.name === 'text') assetSearchValueRef.current = e.value ?? '';
              },
              itemTemplate: (data: Asset, _index: number, element: any) => {
                const searchValue = assetSearchValueRef.current.trim().toLowerCase();
                const matchedAttributesHtml = searchValue
                  ? assetSearchAttributes
                      .filter(({ field }) =>
                        (data[field] ?? '').toString().toLowerCase().includes(searchValue),
                      )
                      .map(
                        ({ field, label }) =>
                          `<b>${translate!(label, transFormName)}:</b> ${data[field]}`,
                      )
                      .join(' ')
                  : '';
                const matchedAttributes = matchedAttributesHtml
                  ? `<div style="font-size: 11px;"><i>${matchedAttributesHtml}</i></div>`
                  : '';
                element.innerHTML = `<div>${data?.name ?? ''}</div>${matchedAttributes}`;
              },
            }}
          >
            <Label text={translate!('Asset', transFormName)} />
            <RequiredRule message={translate!('Asset is required', transFormName)} />
          </SimpleItem>
          {isEdit ? (
            <SimpleItem
              dataField="typeNameTranslated"
              editorType="dxTextBox"
              editorOptions={{
                readOnly: true,
              }}
            >
              <Label text={translate!('Document type', transFormName)} />
            </SimpleItem>
          ) : (
            <SimpleItem
              dataField="typeId"
              editorType="dxSelectBox"
              editorOptions={{
                ...dxSelectBoxConfig,
                valueExpr: 'id',
                displayExpr: 'nameTranslated',
                items: documentTypes,
                onValueChanged: handleDocumentTypeChanged,
                readOnly: isReadOnly,
              }}
            >
              <Label text={translate!('Document type', transFormName)} />
              <RequiredRule message={translate!('Select document type', transFormName)} />
            </SimpleItem>
          )}
          <SimpleItem
            dataField="capacityCategoryId"
            editorType="dxSelectBox"
            editorOptions={{
              ...dxSelectBoxConfig,
              valueExpr: 'id',
              displayExpr: 'name',
              items: capacityCategories,
              readOnly: isReadOnly,
            }}
          >
            <RequiredRule message={translate!('Capacity category required', transFormName)} />
            <Label text={translate!('Capacity category', transFormName)} />
          </SimpleItem>
          {isTaskNameCombo ? (
            <SimpleItem
              dataField="taskName"
              editorType="dxSelectBox"
              editorOptions={{
                ...dxSelectBoxConfig,
                valueExpr: 'name',
                displayExpr: 'name',
                readOnly: isReadOnly,
                dataSource: prepareTasknamesData(),
                // @ts-ignore
                grouped: localTaskNames?.some((item: TaskName) => item.groupName),
              }}
            >
              <Label text={translate!('Task name', transFormName)} />
              <RequiredRule message={translate!('Enter task name', transFormName)} />
            </SimpleItem>
          ) : (
            <SimpleItem
              dataField="taskName"
              editorType="dxTextBox"
              editorOptions={{
                readOnly: isReadOnly,
              }}
            >
              <Label text={translate!('Task name', transFormName)} />
              <RequiredRule message={translate!('Enter task name', transFormName)} />
            </SimpleItem>
          )}
          {isEdit && (
            <SimpleItem
              cssClass={`dropdown-user-color ${userColorClass}`}
              dataField="responsiblePersonId"
              editorType="dxSelectBox"
              editorOptions={{
                ...dxSelectBoxConfig,
                valueExpr: 'personalNumber',
                displayExpr: 'fullName',
                items: responsiblePersons,
                readOnly: true,
                onValueChanged: (e) => handleResponsiblePersonChanged(userColors, e.value),
                itemTemplate: (data: any, _index: number, element: any) => {
                  const color = userColors.find((x) => x.userId === data.personalNumber);
                  if (color)
                    element.innerHTML = `<div style="color: #${color.color}">${data.fullName}</div>`;
                  else element.innerHTML = `<div>${data.fullName}</div>`;
                },
              }}
            >
              <Label text={translate!('Responsible person', transFormName)} />
            </SimpleItem>
          )}
          <SimpleItem
            dataField="requiredDate"
            editorType="dxDateBox"
            editorOptions={{
              ...dateFieldOptionsWithSerialization,
              disabledDates: (args) => {
                const date = moment(formatDate(args.date, 'yyyy-MM-dd'));
                const today = moment().startOf('day');
                const _oldRequiredDate = oldRequiredDate
                  ? moment(oldRequiredDate, 'Y-MM-DD')
                  : today;
                if (isEdit) return date.isBefore(today) && !date.isSame(_oldRequiredDate);
                else return date.isBefore(today);
              },
              readOnly: isEdit,
            }}
          >
            <Label text={translate!('Required date', transFormName)} />
            <CustomRule
              validationCallback={(e) => {
                return (
                  moment(e.value).endOf('day').isSameOrAfter(moment().startOf('day')) ||
                  (oldRequiredDate &&
                    oldRequiredDate.toString().substring(0, 10) ===
                      e.value.toString().substring(0, 10))
                );
              }}
              type={'custom'}
              message={translate!(
                'Date is invalid, must be greater then today or today',
                transFormName,
              )}
              ignoreEmptyValue
            />
          </SimpleItem>
          {isShowDowntime && (
            <SimpleItem
              dataField="downtimeFrom"
              editorType="dxDateBox"
              editorOptions={{
                ...dateTimeFieldOptionsWithSerialization,
                readOnly: isReadOnly,
              }}
            >
              <Label text={translate!('Downtime from', transFormName)} />
              <RequiredRule message={translate!('Downtime from required', transFormName)} />
            </SimpleItem>
          )}
          {isShowDowntime && (
            <SimpleItem
              dataField="downtimeTo"
              editorType="dxDateBox"
              editorOptions={{
                ...dateTimeFieldOptionsWithSerialization,
                readOnly: isReadOnly,
              }}
            >
              <Label text={translate!('Downtime to', transFormName)} />
            </SimpleItem>
          )}
          {isShowFaultType && isEdit && (
            <SimpleItem
              dataField="faultTypeId"
              editorType="dxSelectBox"
              editorOptions={{
                ...dxSelectBoxConfig,
                valueExpr: 'id',
                displayExpr: 'name',
                items: faultTypes,
                readOnly: isReadOnly,
              }}
            >
              <Label text={translate!('Fault type', transFormName)} />
            </SimpleItem>
          )}

          {/* Edit */}
          {!isShowFaultType && !isShowDowntime && isEdit && <SimpleItem itemType={'empty'} />}
          {!isShowFaultType && !isShowDowntime && isEdit && <SimpleItem itemType={'empty'} />}
          {isShowFaultType && !isShowDowntime && isEdit && <SimpleItem itemType={'empty'} />}
          {isShowFaultType && isShowDowntime && isEdit && <SimpleItem itemType={'empty'} />}
          {isShowFaultType && isShowDowntime && isEdit && <SimpleItem itemType={'empty'} />}

          {/* Create */}
          {isShowFaultType && isShowDowntime && !isEdit && <SimpleItem itemType={'empty'} />}
          {!isShowFaultType && isShowDowntime && !isEdit && <SimpleItem itemType={'empty'} />}

          <GroupItem colCount={6} colSpan={3}>
            <GroupItem colSpan={isEdit && files && files?.length > 0 ? 4 : 6}>
              <SimpleItem
                colSpan={3}
                dataField="description"
                editorType="dxTextArea"
                editorOptions={{
                  height: 100,
                  readOnly: isReadOnly,
                  maxLength: 2000,
                }}
              >
                <Label text={translate!('Description', transFormName)} />
              </SimpleItem>
              <SimpleItem
                colSpan={3}
                dataField="diagnose"
                editorType="dxTextArea"
                editorOptions={{
                  height: 100,
                  readOnly: isReadOnly,
                  maxLength: 2000,
                }}
              >
                <Label text={translate!('Diagnose', transFormName)} />
              </SimpleItem>
              {worksheets && worksheets.length > 0 && (
                <SimpleItem
                  colSpan={3}
                  dataField="worksheetsSimpleList"
                  editorType="dxTextArea"
                  editorOptions={{
                    height: 100,
                    readOnly: true,
                    value: renderActivities(),
                  }}
                >
                  <Label text={translate!('Activities', transFormName)} />
                </SimpleItem>
              )}
            </GroupItem>
            {/* attachments jen v editu */}
            {isEdit && files && files?.length ? (
              <GroupItem colSpan={2}>
                <div
                  style={{
                    height: '230px',
                    overflow: 'auto',
                    display: 'inline-block',
                  }}
                  className={'file-manager'}
                >
                  {files && defaultFormData.id !== undefined && (
                    <FileFigureList
                      items={files}
                      entityName={'Task'}
                      entityId={defaultFormData.id}
                      isReadOnly={true}
                      onFileDelete={() => {}}
                    />
                  )}
                </div>
              </GroupItem>
            ) : (
              <GroupItem colSpan={2}></GroupItem>
            )}
          </GroupItem>
          {isEdit ? (
            !isReadOnly ? (
              <GroupItem colSpan={3} colCount={10}>
                <GroupItem colSpan={2} colCount={2} cssClass={'desktop-save-edit-buttons'}>
                  <ButtonItem
                    horizontalAlignment={'left'}
                    cssClass={'btn-desktop-save-edit-buttons'}
                  >
                    <ButtonOptions
                      text={translate!('Close', 'Asset.Update')}
                      type={'normal'}
                      width={'100%'}
                      onClick={() => goToBackRoute()}
                    />
                  </ButtonItem>
                  <ButtonItem
                    horizontalAlignment={'left'}
                    cssClass={`btn-desktop-save-edit-buttons`}
                  >
                    <ButtonOptions
                      width={'100%'}
                      type={'success'}
                      text={translate!('Save', transFormName)}
                      onClick={() => setAfterSubmitMode(IAfterSubmitMode.LIST)}
                      useSubmitBehavior
                    />
                  </ButtonItem>
                </GroupItem>
                <GroupItem colSpan={8}>
                  <TaskChangeStatus
                    task={defaultFormData}
                    mutate={handleStatusChanged}
                    responsiblePersons={taskActionRespPersons}
                    showCurrentStatus={false}
                    statuses={statuses}
                    mutateStatuses={mutateStatuses}
                    isOnlyButtons
                    poupWidth={400}
                    isEditForm={isEdit}
                    oldRequiredDate={oldRequiredDate}
                    customValidationFunction={customValidationFunctionActions}
                    taskRatings={taskRatings}
                  />
                </GroupItem>
              </GroupItem>
            ) : (
              <GroupItem colSpan={3} colCount={10}>
                <GroupItem colSpan={2} colCount={1} cssClass={'desktop-save-edit-buttons'}>
                  <ButtonItem
                    horizontalAlignment={'left'}
                    cssClass={'btn-desktop-save-edit-buttons'}
                  >
                    <ButtonOptions
                      text={translate!('Close', 'Asset.Update')}
                      type={'normal'}
                      width={'100%'}
                      onClick={() => goToBackRoute()}
                    />
                  </ButtonItem>
                </GroupItem>
                <GroupItem colSpan={8}>
                  <TaskChangeStatus
                    task={defaultFormData}
                    mutate={handleStatusChanged}
                    responsiblePersons={taskActionRespPersons}
                    showCurrentStatus={false}
                    statuses={statuses}
                    mutateStatuses={mutateStatuses}
                    isOnlyButtons
                    poupWidth={400}
                    isEditForm={isEdit}
                    oldRequiredDate={oldRequiredDate}
                    customValidationFunction={customValidationFunctionActions}
                    taskRatings={taskRatings}
                  />
                </GroupItem>
              </GroupItem>
            )
          ) : (
            <GroupItem colCount={5} colSpan={2}>
              <ButtonItem horizontalAlignment={'left'}>
                <ButtonOptions
                  text={translate!('Close', transFormName)}
                  type={'normal'}
                  width={'100%'}
                  onClick={() => goToBackRoute()}
                />
              </ButtonItem>

              {!statusesToCreate || statusesToCreate.length <= 1 ? (
                <ButtonItem horizontalAlignment={'left'}>
                  <ButtonOptions
                    width={'100%'}
                    type={'success'}
                    text={translate!('Save and continue', transFormName)}
                    useSubmitBehavior
                    disabled={loading}
                    onClick={() => {
                      setAfterSubmitMode(IAfterSubmitMode.CONTINUE);
                      setTargetTaskStatusIdForCreate(null);
                    }}
                  />
                </ButtonItem>
              ) : (
                statusesToCreate.map((status, i) => {
                  return (
                    <ButtonItem key={i} horizontalAlignment={'left'}>
                      <ButtonOptions
                        width={'100%'}
                        type={'success'}
                        text={`${translate!('Save and continue', transFormName)} - ${
                          status.statusToNameTranslated
                        }`}
                        useSubmitBehavior
                        disabled={loading}
                        onClick={() => {
                          setAfterSubmitMode(IAfterSubmitMode.CONTINUE);
                          setTargetTaskStatusIdForCreate(status.statusToId);
                        }}
                      />
                    </ButtonItem>
                  );
                })
              )}
              {!statusesToCreate || statusesToCreate.length <= 1 ? (
                <ButtonItem horizontalAlignment={'left'}>
                  <ButtonOptions
                    width={'100%'}
                    type={'success'}
                    text={translate!('Save and back to list', transFormName)}
                    onClick={() => {
                      setAfterSubmitMode(IAfterSubmitMode.LIST);
                      setTargetTaskStatusIdForCreate(null);
                    }}
                    useSubmitBehavior
                    disabled={loading}
                  />
                </ButtonItem>
              ) : (
                statusesToCreate.map((status, i) => {
                  return (
                    <ButtonItem key={i} horizontalAlignment={'left'}>
                      <ButtonOptions
                        width={'100%'}
                        type={'success'}
                        text={`${translate!('Save and back to list', transFormName)} - ${
                          status.statusToNameTranslated
                        }`}
                        useSubmitBehavior
                        disabled={loading}
                        onClick={() => {
                          setAfterSubmitMode(IAfterSubmitMode.LIST);
                          setTargetTaskStatusIdForCreate(status.statusToId);
                        }}
                      />
                    </ButtonItem>
                  );
                })
              )}
            </GroupItem>
          )}
        </Form>
      </form>
      {prompt}
      {isAssetChangeDialogVisible && <AssetClassChangeDialog />}
    </>
  );
}
